import React, { memo, useContext, useState, useCallback } from 'react'
import { useTranslate } from 'react-admin'
import { CSS } from '@dnd-kit/utilities'
import { useDraggable } from '@dnd-kit/core'
import classNames from 'classnames'
import CloseIcon from '@mui/icons-material/Close'
import { Button, Chip, Menu, MenuItem, Box } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { CheckboxContext } from 'src/components/pages/sales/business-rule/logic/CheckboxContext'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { CustomVariableModal } from 'src/components/pages/sales/business-rule/logic/CustomVariableModal'
import {
  isInCheckedBoxList,
  deleteItemInCheckedBoxList,
  updateItemInCheckBoxList,
} from 'src/components/pages/sales/business-rule/logic/helper'
import { DraggingAssignContext } from 'src/components/pages/sales/business-rule/logic/FormulaComponent'
import { LIQUIDATION } from '../../helper/const'

const LIQUIDATION_COLORS = {
  RETAIL: '#B2FFFF',
  WHOLESALER: '#4cbde3',
  SUPPLIER: '#d58cde',
};

const DataItem = memo(({ item, onClickRemove, varList, formulaLength = 0, ...props }) => {
  const translate = useTranslate()
  const [openModal, setOpenModal] = useState(false)
  const { isSmall } = useContext(DraggingAssignContext)
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    disabled: props.onlyView || openModal,
  })

  const { checkedBox, setCheckedBox } = useContext(CheckboxContext)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)

  const handleMenuOpen = useCallback((event) => {
    setMenuAnchorEl(event.currentTarget)
    event.stopPropagation()
  }, [])

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null)
  }, [])

  const getStyle = useCallback(() => {
    if (transform) {
      transform.scaleX = 1
      transform.scaleY = 1
    }
    return {
      transform: CSS.Transform.toString(transform),
    }
  }, [transform])

  const handleDelete = useCallback(
    (e, key) => {
      if (isInCheckedBoxList(checkedBox, key)) {
        e.stopPropagation()
        let newChecked = [...checkedBox]
        newChecked = deleteItemInCheckedBoxList(checkedBox, key)
        setCheckedBox(newChecked)
      }
    },
    [checkedBox, setCheckedBox]
  )

  const handleUpdate = useCallback(
    (payload, e, key) => {
      if (isInCheckedBoxList(checkedBox, key)) {
        e.stopPropagation()
        let newChecked = [...checkedBox]
        newChecked = updateItemInCheckBoxList(checkedBox, key, payload)
        setCheckedBox(newChecked)
      }
    },
    [checkedBox, setCheckedBox]
  )

  const showValue = (label, value, formulaLength) => {
    if (!props.onlyView && varList) {
      if (item.global_variable) {
        return <span><strong>[VG]</strong> {label}  ({value})</span>
      }
      return `${label}  (${value})`
    }
    if (props.onlyView) {
      return (
        <div style={{ padding: formulaLength > 5 ? '3px 5px' : '8px 12px' }}>
          <span style={{ display: 'block' }}>{item.global_variable && <strong>[VG] </strong>}{label}</span>
          {!props.withoutValues && (
            <span style={{ display: 'block', fontSize: formulaLength > 5 ? '12px' : '20px' }}>{value}</span>
          )}
        </div>
      )
    }
    if (item.global_variable) {
      return <span><strong>[VG]</strong> {label}</span>
    }
    return label
  }

  const getLinearGradient = (colors) => {
    if (colors.length === 1) return colors[0]

    const step = 100 / colors.length

    const stops = colors.flatMap((color, i) => [
      `${color} ${(i * step).toFixed(2)}%`,
      `${color} ${((i + 1) * step).toFixed(2)}%`,
    ])

    return `linear-gradient(135deg, ${stops.join(', ')})`
  }

  const whatKindOfVarIsColor = (variable) => {
   if (!variable?.show) {
      if (variable?.show_sitio) return '#E8C8B3'
      if (variable?.global_variable) return '#70b578'
      return 'transparent'
    }

    const rules = variable.rule_liquidation_type
    if (rules?.length > 0) {
      const colors = Object.entries(LIQUIDATION_COLORS)
        .filter(([type]) => rules.includes(type))
        .map(([, color]) => color)
      return getLinearGradient(colors)
    }

    if (variable?.global_variable) return '#8CAD3C'
    if (variable?.show_sitio) return '#B3D8E8'
    return '#E8E3B3'
  }

  const getLiquidationRuleTypesText = (variable) => {
    if (variable?.rule_liquidation_type && variable?.rule_liquidation_type?.length > 0) {
      let text = []
      if (variable.rule_liquidation_type.includes('RETAIL')) text.push(translate('ra.field.retail'))
      if (variable.rule_liquidation_type.includes('WHOLESALER')) text.push(translate('ra.field.wholesaler'))
      if (variable.rule_liquidation_type.includes('SUPPLIER')) text.push(translate('ra.field.supplier'))
      return text.join(', ')
    }

    return null
  }

  const whatKindOfVarIsToolTip = (variable) => {
    if (variable?.show && variable?.show_sitio) {
      const ruleTypes = getLiquidationRuleTypesText(variable)
      if (ruleTypes) return translate('ra.message.desglose_rule_types_and_sitio_var', { ruleTypes })
      return translate('ra.message.desglose_and_sitio_var')
    }
    if (variable?.show) {
      const ruleTypes = getLiquidationRuleTypesText(variable)
      if (variable?.global_variable) {
        if (ruleTypes) return `${translate('ra.message.global_variable')} - ${translate('ra.message.desglose_rule_types_var', { ruleTypes })}`
        return translate('ra.message.global_variable_and_show')
      }
      if (ruleTypes) return translate('ra.message.desglose_rule_types_var', { ruleTypes })
      return translate('ra.message.desglose_var')
    }
    if (variable?.show_sitio) return translate('ra.message.sitio_var')
    if (variable?.global_variable) return translate('ra.message.global_variable')
    return ''
  }

  return (
    <div
      ref={setNodeRef}
      className={classNames(`data_structure ${props.onlyView ? 'data_item_modal' : ''}`, {
        dragging: isDragging,
        embedded: item.parentId,
        small: isSmall,
      })}
      style={getStyle()}
      {...listeners}
      {...attributes}
    >
      {openModal && (
        <CustomVariableModal
          open={openModal}
          handleClose={() => setOpenModal(false)}
          titleModal={translate('ra.title.update_variable')}
          onSubmit={(payload, e) => {
            handleUpdate(payload, e, item.key)
          }}
          defaultValue={{
            var: {
              label: item.label,
              description: item.description,
              show: item.show,
              show_sitio: item.show_sitio,
              value: item.value,
              rule_liquidation_type: props.selectedRuleType !== LIQUIDATION ? null : item.rule_liquidation_type
            }
          }}
          selectedRuleType={props.selectedRuleType}
          ruleLiquidationTypes={props.ruleLiquidationTypes}
        />
      )}
      <Tooltip title={varList && whatKindOfVarIsToolTip(item)} placement='top'>
        <Box>
          <Chip
            key={item.id}
            label={showValue(item.label, item.value, formulaLength)}
            variant='outlined'
            style={{ background: whatKindOfVarIsColor(item) }}
            onDelete={
              item.isOperator && !props.onlyView && item?.created && !item?.global_variable
                ? (e) => {
                    handleMenuOpen(e)
                  }
                : !item.hasOwnProperty('created') && !props.onlyView
                ? (e) => {
                    handleDelete(e, item.key)
                  }
                : null
            }
            deleteIcon={
              item.isOperator && !props.onlyView && item?.created && !item?.global_variable ? (
                <MoreVertIcon />
              ) : !item.hasOwnProperty('created') ? (
                <CloseIcon fontSize='13' />
              ) : (
                <></>
              )
            }
          />
          <Menu
            anchorEl={menuAnchorEl}
            keepMounted
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
          >
            <MenuItem
              onClick={(e) => {
                setOpenModal(true)
                handleMenuClose()
              }}
            >
              {translate('ra.action.edit')}
            </MenuItem>
            <MenuItem
              onClick={(e) => {
                handleDelete(e, item.key)
                handleMenuClose()
              }}
            >
              {translate('ra.action.delete')}
            </MenuItem>
          </Menu>
        </Box>
      </Tooltip>

      {!item.isOperator && !props.onlyView && (
        <Button
          className={classNames('close_icon', {
            small: isSmall,
          })}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => onClickRemove(item)}
        >
          <CloseIcon fontSize='13' />
        </Button>
      )}
    </div>
  )
})

export default DataItem
